import { useHandleError } from "@/hooks/useHandleError";
import { PrizeColorCodes } from "@/pages/Rounds/constant";
import { pointsToYen } from "@/utils/pointsToYen";
import { LoadingSpinner } from "@roman/shared/components/LoadingSpinner";
import { usePrizePayout } from "@roman/shared/hooks/soccer/usePrizePayout";
import type { Round } from "@roman/shared/types/Round";
import type { PrizeAmount } from "@roman/shared/types/RoundEntryPrize";

export const Prize = ({ round }: { round: Round }) => {
  const roundId = round.id;
  const prizePool = round.prizePool;
  const maxNumEntries = round.maxNumEntries;
  const handleError = useHandleError("賞金額の取得に失敗しました");
  const { data: prizePayoutData, isLoading: isPrizeLoading } = usePrizePayout(roundId, handleError);

  if (!prizePayoutData || isPrizeLoading) {
    return <div>読み込み中...</div>;
  }

  if (prizePayoutData.roundEntryPrize.length === 0) {
    return <div>エラーが発生しました</div>;
  }

  const selectedPayout = prizePayoutData.roundEntryPrize.find((payout) => payout.totalPayOut === prizePool);

  // group ranks with same amount of prize, 1st to 3rd are individual
  const groupRanks = (prizes: PrizeAmount[]) => {
    const grouped: Array<{
      rank: string;
      amount: string;
      percentage: string | null;
    }> = [];
    let i = 0;
    let prizeIndex = 0;
    // add 1st to 3rd individually
    for (i = 1; i < 4; i++) {
      while (prizeIndex < prizes.length) {
        if (prizes[prizeIndex].startRank <= i && i <= prizes[prizeIndex].endRank) {
          grouped.push({
            rank: `${i}`,
            amount: pointsToYen(prizes[prizeIndex].amount),
            percentage: null,
          });
          break;
        }
        prizeIndex++;
      }
    }
    if (prizeIndex >= prizes.length) {
      return grouped;
    }

    const current = prizes[prizeIndex];
    // when current rank includes 3rd place
    if (current.startRank <= 3 && current.endRank >= 4) {
      grouped.push({
        rank: current.endRank === 4 ? "4" : `4-${current.endRank}`,
        amount: pointsToYen(current.amount),
        percentage: ((current.endRank / maxNumEntries) * 100).toFixed(2),
      });
    }
    prizeIndex++;

    // after 4th place
    while (prizeIndex < prizes.length) {
      const current = prizes[prizeIndex];
      const percentage = (current.endRank / maxNumEntries) * 100;

      grouped.push({
        rank:
          current.startRank === current.endRank ? `${current.startRank}` : `${current.startRank}-${current.endRank}`,
        amount: pointsToYen(current.amount),
        percentage: (percentage < 1 ? 1 : percentage).toFixed(0),
      });
      prizeIndex++;
    }

    return grouped;
  };

  const PrizeBar = ({ rank, amount }: { rank: string; amount: string }) => {
    const rankColors = PrizeColorCodes[rank];

    return (
      <div
        className="relative flex items-center my-4"
        style={{
          backgroundColor: rankColors.background,
          height: "60px",
        }}
      >
        <div
          className="absolute w-[78.89px] h-full z-10 flex justify-center items-center  text-white"
          style={{
            backgroundColor: rankColors.foreground,
            clipPath: "polygon(0% 0%, 100% 0%, 77% 100%, 0% 100%)",
          }}
        >
          <div className="flex items-center">
            <span className="text-[38px] font-bold">{rank}</span>
            <span className="text-[18px] font-bold">位</span>
          </div>
        </div>
        <div className="relative flex-1 pr-7 text-right">
          <span className="text-[24px] font-bold text-black">{amount}円</span>
        </div>
      </div>
    );
  };

  const groupedPrizes = selectedPayout ? groupRanks(selectedPayout.prize) : [];
  const rankTill3rd = ["1", "2", "3"];
  return (
    <div>
      {isPrizeLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="mb-5">
          <div className="text-[20px] text-black font-semibold">賞金額</div>
          <div>
            {groupedPrizes.map((prize, index) => {
              if (rankTill3rd.includes(prize.rank)) {
                return (
                  <div key={index}>
                    <PrizeBar rank={prize.rank} amount={prize.amount} />
                    {prize.rank === "3" && <div className="border-b border-dark-gray" />}
                  </div>
                );
              }
              return (
                <div
                  key={`prize-${prize.rank}`}
                  className="flex border-b border-dark-gray px-5 pt-5 pb-4 items-center"
                  style={{
                    height: "60px",
                  }}
                >
                  <div className="flex-col flex justify-left">
                    <p className="text-left text-black w-full">
                      <span className="text-[20px] font-bold">{prize.rank}</span>
                      <span className="text-[18px]"> 位</span>
                    </p>
                    <p className="text-[10px] text-dark-gray text-left">上位{prize.percentage}%</p>
                  </div>
                  <div className="flex-1 text-right">
                    <span className="text-[20px] font-bold">{prize.amount}円</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
